import type { SearchFolderRulesMatch, SearchFolderKind } from './useData';
import { normalizeSearchFolderKind } from './useData';
import { searchFolderPeriodPresetLabel } from '../emails/utils/receivedDatePresets';

export type RuleForSummary = { field: string; operator: string; value: string };

const FIELD_LABELS: Record<string, string> = {
  from: 'From',
  to: 'To',
  cc: 'Cc',
  toOrCc: 'To or Cc',
  fromToCcBcc: 'From, To, Cc or Bcc',
  subject: 'Subject',
  body: 'Body',
};

const OPERATOR_VERB: Record<string, string> = {
  contains: 'contains',
  notContains: 'does not contain',
  is: 'is',
  isNot: 'is not',
  beginsWith: 'starts with',
  endsWith: 'ends with',
};

/** Format a single rule as a short condition line (e.g. "Subject contains \"x\"") */
function formatConditionLine(r: RuleForSummary): string {
  const fieldLabel = FIELD_LABELS[r.field] ?? r.field;
  const verb = OPERATOR_VERB[r.operator] ?? 'contains';
  const value = (r.value ?? '').trim();
  const valueDisplay = value ? `"${value}"` : '(empty)';
  return `${fieldLabel} ${verb} ${valueDisplay}`;
}

/**
 * Structured summary for display: headline + optional condition lines.
 * Use in List (and Form) for clearer layout.
 */
export interface SearchFolderSummaryStructured {
  headline: string;
  matchLabel: null | 'Match all' | 'Match any';
  conditionLines: string[];
  periodLine: string | null;
}

export function formatSearchFolderPeriodLine(
  periodPreset?: string | null,
  periodYear?: number | null,
): string | null {
  return searchFolderPeriodPresetLabel(periodPreset, periodYear);
}

const MAILBOX_KIND_HEADLINES: Record<
  Exclude<SearchFolderKind, 'search'>,
  string
> = {
  inbox: 'Shows inbox messages from the selected account',
  combined: 'Shows merged inbox from selected accounts',
  sent: 'Shows all sent mail from the selected account',
  combinedSent: 'Shows merged sent mail from selected accounts',
  mySent: 'Shows messages you sent through this app from the selected account',
  combinedMySent: 'Shows messages you sent through this app from selected accounts',
};

export function getMailboxKindSummaryStructured(folder: {
  name?: string | null;
  folderKind?: string | null;
  periodPreset?: string | null;
  periodYear?: number | null;
}): SearchFolderSummaryStructured | null {
  const kind = normalizeSearchFolderKind(folder.folderKind);
  if (kind === 'search') return null;
  const name = (folder.name || 'Smart folder').trim() || 'Smart folder';
  return {
    headline: `${MAILBOX_KIND_HEADLINES[kind]} in "${name}"`,
    matchLabel: null,
    conditionLines: [],
    periodLine: formatSearchFolderPeriodLine(folder.periodPreset, folder.periodYear),
  };
}

export function getSearchFolderSummaryStructured(folder: {
  rules?: unknown;
  rulesMatch?: string | null;
  name?: string | null;
  search?: string | null;
  searchIn?: unknown;
  periodPreset?: string | null;
  periodYear?: number | null;
}): SearchFolderSummaryStructured {
  const name = (folder.name || 'Smart folder').trim() || 'Smart folder';
  const rules = getRulesForSummary(folder);

  const valid = rules.filter(
    (r) => (r.value ?? '').trim() !== '' || r.operator === 'isNot' || r.operator === 'notContains'
  );

  const periodLine = formatSearchFolderPeriodLine(folder.periodPreset, folder.periodYear);
  if (valid.length === 0 || folder.rulesMatch === 'allMessages') {
    return {
      headline: `Shows all messages in "${name}"`,
      matchLabel: null,
      conditionLines: [],
      periodLine,
    };
  }

  const matchLabel = folder.rulesMatch === 'any' ? 'Match any' : 'Match all';
  const conditionLines = valid.map(formatConditionLine);
  return {
    headline: `Shows messages in "${name}"`,
    matchLabel,
    conditionLines,
    periodLine,
  };
}

/**
 * Legacy search (single search string): structured summary.
 */
export function getLegacySearchSummaryStructured(folder: {
  name?: string | null;
  search?: string | null;
  searchIn?: unknown;
  periodPreset?: string | null;
  periodYear?: number | null;
}): SearchFolderSummaryStructured | null {
  const search = (folder.search ?? '').trim();
  if (!search) return null;
  const name = (folder.name || 'Smart folder').trim() || 'Smart folder';
  const searchIn = folder.searchIn && Array.isArray(folder.searchIn) ? folder.searchIn : [];
  const inPart = searchIn.length > 0 ? ` in ${(searchIn as string[]).join(', ')}` : '';
  return {
    headline: `Shows messages in "${name}"`,
    matchLabel: null,
    conditionLines: [`Search${inPart}: "${search}"`],
    periodLine: formatSearchFolderPeriodLine(folder.periodPreset, folder.periodYear),
  };
}

/**
 * One-stop structured summary: uses legacy search if present and no rules, else rules-based.
 */
export function getDisplaySummaryStructured(folder: {
  rules?: unknown;
  rulesMatch?: string | null;
  name?: string | null;
  search?: string | null;
  searchIn?: unknown;
  folderKind?: string | null;
  periodPreset?: string | null;
  periodYear?: number | null;
}): SearchFolderSummaryStructured {
  const mailboxSummary = getMailboxKindSummaryStructured(folder);
  if (mailboxSummary) return mailboxSummary;

  const rules = getRulesForSummary(folder);
  const hasRules = rules.length > 0 && rules.some(
    (r) => (r.value ?? '').trim() !== '' || r.operator === 'isNot' || r.operator === 'notContains'
  );
  if (!hasRules) {
    const legacy = getLegacySearchSummaryStructured(folder);
    if (legacy) return legacy;
  }
  return getSearchFolderSummaryStructured(folder);
}

/**
 * Build a human-readable sentence (for form preview / fallback).
 */
export function getSearchFolderSummarySentence(
  rules: RuleForSummary[],
  rulesMatch: SearchFolderRulesMatch | string | null | undefined,
  folderName: string
): string {
  const name = (folderName || 'Smart folder').trim() || 'Smart folder';
  const valid = rules.filter(
    (r) => (r.value ?? '').trim() !== '' || r.operator === 'isNot' || r.operator === 'notContains'
  );
  if (valid.length === 0 || rulesMatch === 'allMessages') {
    return `Shows all messages in "${name}".`;
  }
  const matchWord = rulesMatch === 'any' ? 'any' : 'all';
  const lines = valid.map(formatConditionLine);
  return `Shows messages in "${name}" where ${matchWord} of: ${lines.join('; ')}.`;
}

/** Normalize folder.rules from API (may be array or JSON string) for summary. */
export function getRulesForSummary(folder: {
  rules?: unknown;
  rulesMatch?: string | null;
  name?: string | null;
}): RuleForSummary[] {
  let rules = folder.rules;
  if (typeof rules === 'string') {
    try {
      rules = JSON.parse(rules) as RuleForSummary[];
    } catch {
      return [];
    }
  }
  if (!Array.isArray(rules) || rules.length === 0) return [];
  return rules.map((r: any) => ({
    field: typeof r?.field === 'string' ? r.field : 'subject',
    operator: typeof r?.operator === 'string' ? r.operator : 'contains',
    value: typeof r?.value === 'string' ? r.value : '',
  }));
}
